<template>
    <v-bottom-sheet v-model="openModel" scroll-strategy="none" class="bottom-sheet-centered">
        <div class="sheet">
            <!-- Header -->
            <div class="sheet-header">
                <div class="input-wrap">
                    <v-text-field
                        ref="fieldRef"
                        v-model="q"
                        variant="solo-filled"
                        label="Search tags & keywords"
                        clearable
                        density="comfortable"
                        hide-details
                        autofocus
                        @keyup.esc="emit('close')"
                        @keydown.enter.prevent="submitCurrent"
                    />
                </div>
                <v-btn icon variant="text" @click="emit('close')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- Body -->
            <div class="sheet-body">
                <SuggestList v-if="q.trim().length >= 2" :q="q" @select="onSelect" />
                <TrendingList v-else :limit="20" @select="onSelect" />
            </div>
        </div>
    </v-bottom-sheet>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import TrendingList from './TrendingList.vue';
import SuggestList from './SuggestList.vue';
import SearchService, { TypeaheadItem } from '@/services/SearchService';

const props = defineProps<{
    initialQuery?: string;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select-term', payload: { type: 'tag' | 'key'; term: string }): void;
}>();

// v-model:open from parent
const openModel = defineModel<boolean>('open', { default: false });

const q = ref('');
const suggestions = ref<TypeaheadItem[]>([]);
const loading = ref(false);
const fieldRef = ref<any>(null); // Vuetify component instance exposes .focus()
let inflightId = 0;

// seed + focus whenever the sheet opens
watch(openModel, async (v) => {
    if (!v) return;
    // prefer payload from parent; fall back to previous q
    if (typeof props.initialQuery === 'string') q.value = props.initialQuery;
    await nextTick();
    fieldRef.value?.focus?.();
});

const qTrim = computed(() => q.value.trim().toLowerCase());

function inferKind(text: string): 'tag' | 'key' {
    return text.startsWith('#') ? 'tag' : 'key';
}

function submitCurrent() {
    const text = qTrim.value;
    if (!text) return;

    const first = suggestions.value[0];
    if (first) {
        emit('select-term', { type: first.kind, term: first.term });
        return;
    }
    const kind = inferKind(text);
    const term = kind === 'tag' ? text.replace(/^#/, '') : text;
    emit('select-term', { type: kind, term });
}

watch(
    qTrim,
    async (text) => {
        suggestions.value = [];
        if (!text) return;

        const thisId = ++inflightId;
        loading.value = true;
        try {
            const resp = await SearchService.typeahead(text, 12);
            if (thisId !== inflightId) return;

            const forceKind: 'tag' | 'key' | null = text.startsWith('#') ? 'tag' : null;
            suggestions.value = (resp.items || []).map((it) => ({
                kind: forceKind ?? it.kind,
                term: it.term,
                count: it.count,
                sample_thumb: it.sample_thumb,
                sample_route: it.sample_route,
            }));
        } finally {
            if (thisId === inflightId) loading.value = false;
        }
    },
    { immediate: false }
);

function onSelect(payload: { type: 'tag' | 'key'; term: string }) {
    emit('select-term', payload);
}
</script>

<style scoped lang="scss">
.sheet {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    width: 100%;
    max-width: 750px;
    margin: 0 auto;
    background: #111;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    overflow: hidden;
}
.sheet-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 12px 8px;
}
.input-wrap {
    flex: 1;
}
.sheet-body {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 12px 12px;
}
</style>
